import { Badge, Banner, BrandLoader, Button, Icon, type BadgeTone } from '@acs/ui';
import { useEffect, useRef, useState } from 'react';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { formatPhoneDisplay } from '../../shared/lib/phoneFormat.js';
import { useI18n } from '../../i18n/index.js';
import type { TranslationKey } from '../../i18n/index.js';
import { useServices } from '../settings/hooks.js';
import { CallWave, type Speaker, type WavePhase } from './CallWave.js';
import { collectFromTool, hasCollected, type Collected } from './collected.js';
import { useBrowserCall, type CallStatus } from './useBrowserCall.js';

/** `75` → `01:15`. */
export const clock = (seconds: number): string =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

/** Seconds on the line: ticks while live, frozen once the call ends. */
function useCallSeconds(liveAt: number | null, endedAt: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (liveAt == null || endedAt != null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [liveAt, endedAt]);
  if (liveAt == null) return 0;
  return Math.max(0, Math.floor(((endedAt ?? now) - liveAt) / 1000));
}

/** What the status pill says, from the call's state and who was heard last. */
function pill(status: CallStatus, speaker: Speaker, last: Speaker): { key: TranslationKey; tone: BadgeTone } {
  if (status === 'connecting') return { key: 'tryCall.status.ringing', tone: 'info' };
  if (status === 'ended') return { key: 'tryCall.status.ended', tone: 'neutral' };
  if (status === 'error') return { key: 'testCall.status.error', tone: 'danger' };
  if (status !== 'live') return { key: 'tryCall.status.ready', tone: 'neutral' };
  if (speaker === 'ai') return { key: 'tryCall.status.speaking', tone: 'success' };
  if (speaker === 'you') return { key: 'tryCall.status.listening', tone: 'info' };
  if (last === 'you') return { key: 'tryCall.status.thinking', tone: 'info' };
  if (last === 'ai') return { key: 'tryCall.status.listening', tone: 'info' };
  return { key: 'tryCall.status.connected', tone: 'success' };
}

/**
 * Setup's "Try it" call (design: Onboarding · Try it): a throwaway browser call
 * that writes nothing (`demo`). A call stage with the assistant's card, a
 * status pill and timer, a wave that follows both voices, and — instead of a
 * summary — the booking details the assistant collected as the call goes,
 * plus the transcript on request once the call is over.
 */
export function TryCallStage({
  business,
  onAdjustAssistant,
  onStatus,
}: {
  business: BusinessDoc;
  /** "Not quite right? Adjust assistant" — back to the assistant step. */
  onAdjustAssistant: () => void;
  onStatus?: (status: CallStatus) => void;
}) {
  const { t, tag } = useI18n();
  const { data: services } = useServices(business.id);
  const servicesRef = useRef(services);
  servicesRef.current = services;

  const [collected, setCollected] = useState<Collected>({});
  const [speaker, setSpeaker] = useState<Speaker>(null);
  const [lastSpeaker, setLastSpeaker] = useState<Speaker>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  const call = useBrowserCall({
    business,
    demo: true,
    onTool: (name, args, result) =>
      setCollected((prev) =>
        collectFromTool(prev, name, args, result, (id) => servicesRef.current?.find((s) => s.id === id)?.name),
      ),
  });
  const { status, error, turns, liveAt, endedAt, audioRef } = call;
  const seconds = useCallSeconds(liveAt, endedAt);

  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  useEffect(() => onStatusRef.current?.(status), [status]);

  const start = () => {
    setCollected({});
    setSpeaker(null);
    setLastSpeaker(null);
    setShowTranscript(false);
    void call.start();
  };
  const heard = (who: Speaker) => {
    setSpeaker(who);
    if (who) setLastSpeaker(who);
  };

  const phase: WavePhase = status === 'error' ? 'idle' : status;
  const p = pill(status, speaker, lastSpeaker);
  const spoken = turns.filter((x) => x.role !== 'tool');
  const language = languageName(business.language, tag);
  const showData = status === 'ended' || (status === 'live' && hasCollected(collected));

  return (
    <div className="try-stage">
      <div className="try-stage__head">
        <span className="try-stage__avatar" aria-hidden="true">
          {business.name.trim().charAt(0).toUpperCase() || 'A'}
        </span>
        <div className="try-stage__who">
          <div className="try-stage__name">{t('tryCall.assistant')}</div>
          <div className="try-stage__sub">{language ? `${business.name} · ${language}` : business.name}</div>
        </div>
        <Badge dot tone={p.tone} role="status">
          {t(p.key)}
        </Badge>
        {(status === 'live' || status === 'ended') && (
          <span className="try-stage__timer" aria-label={t('tryCall.field.duration')}>
            {clock(seconds)}
          </span>
        )}
      </div>

      <CallWave
        phase={phase}
        remote={call.remoteStream}
        mic={call.micStream}
        context={call.audioContext}
        onSpeaker={heard}
      />

      {(status === 'idle' || status === 'error') && (
        <div className="try-stage__idle">
          {error && (
            <div className="try-stage__error">
              <Banner tone="danger" title={t('testCall.failed')}>{error}</Banner>
            </div>
          )}
          <Button size="lg" className="try-stage__pill" onClick={start} leadingIcon={<Icon name="phone" size={18} />}>
            {t('tryCall.start')}
          </Button>
          <div className="try-stage__note">
            <Icon name="mic" size={14} /> {t('tryCall.note')}
          </div>
        </div>
      )}

      {status === 'connecting' && (
        <div className="try-stage__connecting">
          <BrandLoader size={18} label={t('tryCall.connecting')} /> {t('tryCall.connecting')}
        </div>
      )}

      {status === 'live' && (
        <div className="try-stage__live">
          <Button
            variant="danger-solid"
            className="try-stage__pill"
            onClick={call.stop}
            leadingIcon={<Icon name="phone" size={17} className="try-stage__hangup" />}
          >
            {t('testCall.end')}
          </Button>
        </div>
      )}

      {showData && (
        <dl className="try-stage__data">
          <Datum label={t('tryCall.field.duration')} value={clock(seconds)} />
          <Datum label={t('tryCall.field.service')} value={collected.service} />
          <Datum label={t('tryCall.field.when')} value={when(collected, tag)} />
          <Datum label={t('tryCall.field.name')} value={collected.name} />
          {/* A real call takes the number from caller ID, so the assistant
              never has to ask for it; this demo call has none to show. Said
              — scoped to real calls, which is the only place it is true —
              rather than left as a dash, which read as "it forgot to ask". */}
          <Datum
            label={t('tryCall.field.phone')}
            value={formatPhoneDisplay(collected.phone) ?? undefined}
            placeholder={t('tryCall.field.phoneAuto')}
          />
        </dl>
      )}

      {status === 'ended' && (
        <div className="try-stage__actions">
          <Button variant="secondary" className="try-stage__pill" onClick={start} leadingIcon={<Icon name="refresh" size={16} />}>
            {t('tryCall.again')}
          </Button>
          <Button variant="link" onClick={() => setShowTranscript((v) => !v)} aria-expanded={showTranscript}>
            {t(showTranscript ? 'tryCall.hideTranscript' : 'tryCall.showTranscript')}
          </Button>
          <span className="try-stage__adjust">
            {t('tryCall.adjustPrompt')}{' '}
            <Button variant="link" onClick={onAdjustAssistant}>
              {t('tryCall.adjust')}
            </Button>
          </span>
        </div>
      )}

      {/* After the call only. Offered mid-call it pulled the owner's eyes to
          reading text while they should be talking, and a transcript still
          being written is a moving target to read anyway. */}
      {showTranscript && status === 'ended' && (
        <div className="try-stage__transcript">
          {spoken.length === 0 ? (
            <p className="muted">{t('tryCall.noTranscript')}</p>
          ) : (
            spoken.map((turn, i) => (
              <div key={i} className={`try-stage__line is-${turn.role}`}>
                <span className="try-stage__speaker">{t(turn.role === 'you' ? 'testCall.you' : 'tryCall.ai')}</span>
                <span>{turn.text}</span>
              </div>
            ))
          )}
        </div>
      )}

      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} autoPlay />
    </div>
  );
}

function Datum({ label, value, placeholder = '—' }: { label: string; value: string | undefined; placeholder?: string }) {
  return (
    <div className="try-stage__datum">
      <dt>{label}</dt>
      <dd className={value ? undefined : 'is-empty'}>{value ?? placeholder}</dd>
    </div>
  );
}

/** `2026-10-16` + `14:30` → "Fri, 16 Oct · 14:30" in the owner's language. */
function when(c: Collected, tag: string): string | undefined {
  if (!c.date) return c.time;
  const d = new Date(`${c.date}T12:00:00`);
  const day = Number.isNaN(d.getTime())
    ? c.date
    : d.toLocaleDateString(tag, { weekday: 'short', day: 'numeric', month: 'short' });
  return c.time ? `${day} · ${c.time}` : day;
}

/** The business's call language, named in the owner's UI language. */
function languageName(code: string, tag: string): string | undefined {
  try {
    const name = new Intl.DisplayNames([tag], { type: 'language' }).of(code);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : undefined;
  } catch {
    return undefined;
  }
}
